import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The back office's words for flags, queues and compute (epic 09, epic 12). `ops-copy.ts` puts
// them together with the rest into the one catalogue the screens read.

export type PlatformCatalog = typeof EN;

const EN = {
  // Flags (S8.06, S12.06).
  flagsTitle: "Flags",
  flagsDescription: "What each flag turns on, its default, and who overrides it.",
  emptyFlags: "No flag is catalogued.",
  columnFlag: "Flag",
  columnDefault: "Default",
  columnPlatform: "Platform",
  columnOverrides: "Overrides",
  setOverrideAction: "Set an override",
  setOverrideTitle: "Set an override",
  setOverrideDescription:
    "Turns a flag on or off for one organization, or for the whole platform. The organization's admins see it in their audit log.",
  setOverrideSubmit: "Save override",
  fieldFlag: "Flag",
  fieldOrganization: "Organization",
  organizationScopeHint: "Leave empty to set it for the whole platform.",
  wholePlatform: "The whole platform",
  fieldEnabled: "Turned on",
  flagOverridesTitle: "Who overrides it",
  emptyFlagOverrides: "No organization overrides this flag.",
  platformOverride: "Platform override",
  clearAction: "Clear",
  clearTitle: "Clear this override",
  clearDescription: "The flag goes back to the platform's value, or its default.",
  clearSubmit: "Clear override",
  orphansTitle: "Overrides for removed flags",
  orphansDescription: "Stored overrides naming a flag no longer in the catalogue. Clear them.",
  toastOverrideSaved: "Override saved",
  toastOverrideCleared: "Override cleared",
  errorOverrideTitle: "The override did not change",
  searchOrganizationsShort: "Search organizations",

  // Queues (S9.07, S9.08, S12.05).
  queuesTitle: "Queues",
  queuesDescription:
    "Each lane's work, parked streams, overdue wakes and dead letters. Refreshes every few seconds.",
  lanesTitle: "Lanes",
  emptyLanes: "No lanes are installed.",
  columnHealth: "Health",
  columnLane: "Lane",
  columnPending: "Waiting",
  overdueWakesTitle: "Overdue wakes",
  emptyOverdueWakes: "No wake is overdue.",
  columnWake: "Process",
  columnOverdueBy: "Overdue by",
  columnDue: "Due",
  deadLettersTitle: "Dead letters",
  emptyDeadLetters: "No dead letters.",
  columnJob: "Job",
  columnAttempts: "Attempts",
  columnBuried: "Gave up",
  deadLetterTitle: "Dead letter",
  deadLetterWhy: "Why it gave up",
  failureStatus: "Provider answered",
  deadLetterStream: "Stream",
  deadLetterEvent: "Event",
  redriveAction: "Redrive",
  discardAction: "Discard",
  redriveTitle: "Redrive this job?",
  redriveDescription: "It runs again from the start. The audit keeps your reason.",
  discardTitle: "Discard this job?",
  discardDescription: "It never runs again. The audit keeps your reason.",
  toastRedriven: "Job redriven",
  toastDiscarded: "Job discarded",
  errorDeadLetterTitle: "Nothing changed",
  errorDeadLetterGone: "That dead letter is gone: someone may have handled it already.",
  // Parked streams (eventing ADR-001, revision 1).
  parkedStreamsTitle: "Parked streams",
  emptyParkedStreams: "No stream is parked.",
  columnStream: "Stream",
  columnFailures: "Failures in a row",
  columnParked: "Parked",
  parkedStreamTitle: "Parked stream",
  parkedStreamWhy: "Why it parked",
  parkedStreamThreshold: "Parks after",
  resumeAction: "Resume",
  resumeTitle: "Resume this stream?",
  resumeDescription:
    "Its jobs run again in order, starting with the one that kept failing. The audit keeps your reason.",
  toastResumed: "Stream resumed",
  resumeLaneAction: "Resume the whole lane",
  resumeLaneTitle: "Resume every parked stream of this lane?",
  resumeLaneDescription:
    "For after an outage: every stream parked on this lane runs again in order. The audit keeps your reason and how many.",
  toastLaneResumed: { one: "1 stream resumed", other: "{count} streams resumed" },
  errorStreamGone: "That stream is not parked any more: someone may have resumed it already.",

  // Dead intents (S26.10, ADR-022 point 8).
  deadIntentsTitle: "Dead intents",
  emptyDeadIntents: "No dead intents.",
  columnManager: "Manager",
  columnKey: "Key",
  deadIntentTitle: "Dead intent",
  deadIntentWhy: "Why it gave up",
  replayAction: "Replay",
  replayTitle: "Replay this intent?",
  replayDescription: "It runs again from its first attempt. The audit keeps your reason.",
  discardIntentTitle: "Discard this intent?",
  discardIntentDescription: "It never runs again. The audit keeps your reason.",
  toastReplayed: "Intent replayed",
  toastIntentDiscarded: "Intent discarded",
  errorIntentGone: "That dead intent is gone: someone may have handled it already.",

  // Compute (S9.09, S12.05).
  computeTitle: "Compute",
  computeDescription: "The workers and the providers they call. Refreshes every few seconds.",
  providersTitle: "Providers",
  emptyProviders: "No provider has reported.",
  columnProvider: "Provider",
  columnTrouble: "Trouble",
  columnRetry: "Retries",
  troubleRateLimited: "Rate limited",
  troubleUnavailable: "Unavailable",
  troubleRefused: "Refused",
  workersTitle: "Workers",
  workersLive: { one: "1 live", other: "{count} live" },
  workersStale: { one: "1 stale", other: "{count} stale" },
  emptyLiveWorkers: "No worker is running.",
  emptyStaleWorkers: "No stale workers.",
  columnWorker: "Worker",
  columnHost: "Host",
  workerTitle: "Worker",
  factVersion: "Version",
  factStarted: "Started",
  factPipelines: "Pipelines",
  factParallelism: "Parallelism",
} as const;

const ES: Translation<PlatformCatalog> = {
  // Flags (S8.06, S12.06).
  flagsTitle: "Flags",
  flagsDescription: "Qué activa cada flag, su valor por defecto y quién lo sobrescribe.",
  emptyFlags: "No hay ningún flag en el catálogo.",
  columnFlag: "Flag",
  columnDefault: "Por defecto",
  columnPlatform: "Plataforma",
  columnOverrides: "Sobrescrituras",
  setOverrideAction: "Definir una sobrescritura",
  setOverrideTitle: "Definir una sobrescritura",
  setOverrideDescription:
    "Activa o desactiva un flag para una organización o para toda la plataforma. Los administradores de la organización lo ven en su registro de auditoría.",
  setOverrideSubmit: "Guardar sobrescritura",
  fieldFlag: "Flag",
  fieldOrganization: "Organización",
  organizationScopeHint: "Déjalo vacío para definirlo en toda la plataforma.",
  wholePlatform: "Toda la plataforma",
  fieldEnabled: "Activado",
  flagOverridesTitle: "Quién lo sobrescribe",
  emptyFlagOverrides: "Ninguna organización sobrescribe este flag.",
  platformOverride: "Sobrescritura de la plataforma",
  clearAction: "Quitar",
  clearTitle: "Quitar esta sobrescritura",
  clearDescription: "El flag vuelve al valor de la plataforma, o al que tiene por defecto.",
  clearSubmit: "Quitar sobrescritura",
  orphansTitle: "Sobrescrituras de flags eliminados",
  orphansDescription: "Sobrescrituras guardadas de un flag que ya no está en el catálogo. Quítalas.",
  toastOverrideSaved: "Sobrescritura guardada",
  toastOverrideCleared: "Sobrescritura quitada",
  errorOverrideTitle: "La sobrescritura no cambió",
  searchOrganizationsShort: "Buscar organizaciones",

  // Queues (S9.07, S9.08, S12.05).
  queuesTitle: "Colas",
  queuesDescription:
    "El trabajo de cada carril, los streams detenidos, las activaciones atrasadas y los dead letters. Se actualiza cada pocos segundos.",
  lanesTitle: "Carriles",
  emptyLanes: "No hay carriles instalados.",
  columnHealth: "Salud",
  columnLane: "Carril",
  columnPending: "En espera",
  overdueWakesTitle: "Activaciones atrasadas",
  emptyOverdueWakes: "Ninguna activación está atrasada.",
  columnWake: "Proceso",
  columnOverdueBy: "Atraso",
  columnDue: "Debía correr",
  deadLettersTitle: "Dead letters",
  emptyDeadLetters: "Sin dead letters.",
  columnJob: "Trabajo",
  columnAttempts: "Intentos",
  columnBuried: "Se rindió",
  deadLetterTitle: "Dead letter",
  deadLetterWhy: "Por qué se rindió",
  failureStatus: "El proveedor respondió",
  deadLetterStream: "Stream",
  deadLetterEvent: "Evento",
  redriveAction: "Reintentar",
  discardAction: "Descartar",
  redriveTitle: "¿Reintentar este trabajo?",
  redriveDescription: "Corre de nuevo desde el principio. La auditoría guarda tu motivo.",
  discardTitle: "¿Descartar este trabajo?",
  discardDescription: "No vuelve a correr nunca. La auditoría guarda tu motivo.",
  toastRedriven: "Trabajo reintentado",
  toastDiscarded: "Trabajo descartado",
  errorDeadLetterTitle: "No cambió nada",
  errorDeadLetterGone: "Ese dead letter ya no está: puede que alguien lo haya atendido.",
  // Parked streams (eventing ADR-001, revision 1).
  parkedStreamsTitle: "Streams detenidos",
  emptyParkedStreams: "Ningún stream está detenido.",
  columnStream: "Stream",
  columnFailures: "Fallos seguidos",
  columnParked: "Se detuvo",
  parkedStreamTitle: "Stream detenido",
  parkedStreamWhy: "Por qué se detuvo",
  parkedStreamThreshold: "Se detiene tras",
  resumeAction: "Reanudar",
  resumeTitle: "¿Reanudar este stream?",
  resumeDescription:
    "Sus trabajos corren de nuevo en orden, empezando por el que seguía fallando. La auditoría guarda tu motivo.",
  toastResumed: "Stream reanudado",
  resumeLaneAction: "Reanudar todo el carril",
  resumeLaneTitle: "¿Reanudar todos los streams detenidos de este carril?",
  resumeLaneDescription:
    "Para después de una caída: cada stream detenido en este carril corre de nuevo en orden. La auditoría guarda tu motivo y cuántos.",
  toastLaneResumed: { one: "1 stream reanudado", other: "{count} streams reanudados" },
  errorStreamGone: "Ese stream ya no está detenido: puede que alguien ya lo haya reanudado.",

  // Dead intents (S26.10, ADR-022 point 8).
  deadIntentsTitle: "Dead intents",
  emptyDeadIntents: "Sin dead intents.",
  columnManager: "Gestor",
  columnKey: "Clave",
  deadIntentTitle: "Dead intent",
  deadIntentWhy: "Por qué se rindió",
  replayAction: "Reintentar",
  replayTitle: "¿Reintentar este intent?",
  replayDescription: "Corre de nuevo desde su primer intento. La auditoría guarda tu motivo.",
  discardIntentTitle: "¿Descartar este intent?",
  discardIntentDescription: "No vuelve a correr nunca. La auditoría guarda tu motivo.",
  toastReplayed: "Intent reintentado",
  toastIntentDiscarded: "Intent descartado",
  errorIntentGone: "Ese dead intent ya no está: puede que alguien ya lo haya atendido.",

  // Compute (S9.09, S12.05).
  computeTitle: "Cómputo",
  computeDescription: "Los workers y los proveedores que llaman. Se actualiza cada pocos segundos.",
  providersTitle: "Proveedores",
  emptyProviders: "Ningún proveedor ha reportado.",
  columnProvider: "Proveedor",
  columnTrouble: "Problema",
  columnRetry: "Reintentos",
  troubleRateLimited: "Límite de uso",
  troubleUnavailable: "No disponible",
  troubleRefused: "Rechazado",
  workersTitle: "Workers",
  workersLive: { one: "1 activo", other: "{count} activos" },
  workersStale: { one: "1 inactivo", other: "{count} inactivos" },
  emptyLiveWorkers: "Ningún worker está corriendo.",
  emptyStaleWorkers: "Sin workers inactivos.",
  columnWorker: "Worker",
  columnHost: "Host",
  workerTitle: "Worker",
  factVersion: "Versión",
  factStarted: "Inicio",
  factPipelines: "Pipelines",
  factParallelism: "Paralelismo",
};

export const PLATFORM_CATALOGS: Catalogs<PlatformCatalog> = { en: EN, es: ES };
